import { apiClient } from './client'
import { ApiResponse } from './types'

export interface AffiliateDetails {
  id: string
  userId: string
  displayName: string
  companyName?: string
  // #701: structured Company & Bank details surfaced on the affiliate-detail
  // response so admins can VIEW them anywhere bank/company details are shown
  // (the scoped /bank-accounts endpoint only ever returns the viewer's OWN
  // accounts, so admins viewing another affiliate must read these instead).
  companyType?: CompanyType
  companyRegistrationNumber?: string
  companyAddress?: string
  // Nested bank account, or null when the affiliate hasn't added one. The raw
  // account number is never returned — accountNumberMasked is the last-4 mask.
  // bankCode = Israeli Bank Number (מספר בנק); branchNumber = Branch (מספר סניף).
  bankAccount?: {
    accountHolderName?: string
    bankName?: string
    bankCode?: string
    branchNumber?: string
    accountNumberMasked?: string
    iban?: string
    swiftCode?: string
  } | null
  // #839: the legacy raw affiliates.bank_account_details JSONB, kept for
  // onboarded/imported affiliates whose bank never landed in bank_accounts.
  // snake_case keys, and the plaintext account number is stripped server-side
  // and replaced by accountNumberMasked. #1321 relies on this as the last tier
  // when deciding whether the Company & Bank warning banner should show.
  bankDetails?: {
    account_holder?: string
    bank_name?: string
    bank_code?: string
    branch_code?: string
    accountNumberMasked?: string
    iban?: string
    swift?: string
    swift_code?: string
  } | null
  // Mirror of the top-level affiliate record status; surfaced on the
  // details page header so admins can see pending/suspended/rejected at a
  // glance. accountStatus is the derived/displayed variant.
  status?: 'active' | 'pending' | 'suspended' | 'rejected'
  // #572: "Affiliate Type" on About Me — persisted to the affiliate's
  // business_type column. Value is one of AFFILIATE_TYPE_FILTER_OPTIONS labels.
  affiliateType?: string
  description?: string
  // #355: entity-level public note shown to every partnered hotel via the
  // Hotel Notes column on /hotel-affiliations.
  publicNotes?: string
  segments: string[]
  // #524: optional region_options codes (same tree as the hotel side).
  regions?: string[]
  website?: string
  socialAccounts: {
    facebook?: string
    instagram?: string
    tiktok?: string
    youtube?: string
    twitter?: string
    linkedin?: string
  }
  topMedia?: {
    id: string
    type: 'image' | 'video' | 'link'
    url: string
    title?: string
    views?: number
  }[]
  // #674 — affiliate contact details (contact_details JSONB). contactName is
  // the editable "Full Name" on the contact card.
  contactDetails?: {
    contactName?: string
    primaryEmail?: string
    secondaryEmail?: string
    phone?: string
    alternatePhone?: string
    address?: {
      street?: string
      city?: string
      state?: string
      country?: string
      postalCode?: string
    }
  }
  profileImage?: string
  coverImage?: string
  profileCompleteness: number
  accountStatus: 'active' | 'pending' | 'suspended' | 'rejected'
  verificationStatus: 'verified' | 'unverified' | 'pending'
  totalFollowers: number
  totalEngagement: number
  createdAt: string
  updatedAt: string
  /**
   * #1416 / #2469 — the affiliate's DEFAULT revenue share for new
   * affiliations, ALWAYS a FRACTION in [0,1] (0.5 = 50%), never a percent.
   * Null = no default ("Not set"). Display with formatRevsharePercent() from
   * @/utils/revshare; do NOT run it through commissionRateAsPercent().
   */
  revshare?: number | null
}

export interface UpdateRevshareResult {
  affiliateId: string
  revshare: number | null
  previousRevshare: number | null
}

/**
 * #2469 — a revenue-share period of an affiliate: an affiliation approved on
 * a day in [startsOn, endsOn) gets `revshare` instead of the default, fixed at
 * approval. benefit_months = 70% from the onboarding contract signing;
 * admin = a special period set by an admin.
 */
export interface AffiliateRevsharePeriod {
  id: string
  affiliateId: string
  /** Fraction 0 < r <= 1. */
  revshare: number
  /** Israel date (YYYY-MM-DD), first day covered. */
  startsOn: string
  /** Israel date (YYYY-MM-DD), first day NOT covered. */
  endsOn: string
  kind: 'benefit_months' | 'admin'
  onboardingRequestId: string | null
  reason: string
  createdBy: string
  createdAt: string
}

interface UpdateAffiliateDetailsPayload {
  displayName?: string
  // #572: maps to the affiliate's business_type column server-side.
  affiliateType?: string
  description?: string
  publicNotes?: string
  segments?: string[]
  // #524: optional region list; [] clears it.
  regions?: string[]
  website?: string
  socialAccounts?: {
    facebook?: string
    instagram?: string
    tiktok?: string
    youtube?: string
    twitter?: string
    linkedin?: string
  }
  topMedia?: {
    id: string
    type: 'image' | 'video' | 'link'
    url: string
    title?: string
    views?: number
  }[]
  // #674 — editable contact details payload (contact_details JSONB).
  contactDetails?: {
    contactName?: string
    primaryEmail?: string
    secondaryEmail?: string
    phone?: string
    alternatePhone?: string
    address?: {
      street?: string
      city?: string
      state?: string
      country?: string
      postalCode?: string
    }
  }
}

// #275 v0 — manual self-reported creator stats. `source` is 'self' in v0.
export interface CreatorStat {
  platform: string
  followers: number
  avgEngagementRate: number | null
  source: 'self' | 'admin' | 'api'
  lastUpdatedAt: string
}

export interface CreatorStatInput {
  platform: string
  followers: number
  avgEngagementRate?: number | null
}

// #572 — Company (legal entity) details. COMPANY-ONLY: bank/payout data lives
// in the existing bank_accounts store (fronted by payoutsApi in affiliates.api),
// NOT here. See AffiliateDetailsPage for the bank-accounts wiring.
export type CompanyType =
  | 'company_ltd_corporation'
  | 'self_employed_registered'
  | 'self_employed_below_threshold'
  | 'no_registered_business'

export interface AffiliateBilling {
  affiliateId: string
  companyName: string
  companyType: CompanyType
  companyRegistrationNumber: string
  companyAddress: string
}

export type AffiliateBillingInput = Omit<AffiliateBilling, 'affiliateId'>

/**
 * #1105 — Open Graph unfurl payload for a Top Media link item (mirrors
 * partner-service LinkPreviewService.TopMediaLinkPreview). Metadata only
 * (ADR-010): imageUrl/faviconUrl are SELF-HOSTED re-encoded copies, and
 * `domain` is the FINAL resolved hostname after redirects.
 */
export interface TopMediaLinkPreview {
  ok: boolean
  url: string
  domain: string
  siteName?: string
  title?: string
  description?: string
  imageUrl?: string
  imageWidth?: number
  imageHeight?: number
  faviconUrl?: string
}

export const affiliateDetailsApi = {
  /**
   * Get current user's affiliate profile
   * Uses /affiliates/me endpoint
   */
  getMyDetails: (): Promise<ApiResponse<AffiliateDetails>> =>
    apiClient.get('/affiliates/me'),

  /**
   * Get affiliate details by ID
   * Uses /affiliates/:affiliateId endpoint
   */
  getDetails: (affiliateId: string): Promise<ApiResponse<AffiliateDetails>> =>
    apiClient.get(`/affiliates/${affiliateId}`),

  /**
   * Update current user's affiliate profile
   */
  updateMyDetails: (payload: UpdateAffiliateDetailsPayload): Promise<ApiResponse<AffiliateDetails>> =>
    apiClient.put('/affiliates/me', payload),

  /**
   * #1416 / #2469 — set the affiliate's DEFAULT revenue share for new
   * affiliations (existing ones never change through it). ADMIN ONLY (the
   * endpoint is guarded by authorizeRoles(['admin','global_admin'])).
   *
   * `revshare` is a FRACTION in [0,1] — divide the percent the admin typed by
   * 100 before calling — or null to clear the default ("Not set").
   */
  updateRevshare: (
    affiliateId: string,
    revshare: number | null
  ): Promise<ApiResponse<UpdateRevshareResult>> =>
    apiClient.put(`/affiliates/${affiliateId}/revshare`, { revshare }),

  /** #2469 — the affiliate's live revenue-share periods (admin only). */
  getRevsharePeriods: (
    affiliateId: string
  ): Promise<ApiResponse<{ periods: AffiliateRevsharePeriod[] }>> =>
    apiClient.get(`/affiliates/${affiliateId}/revshare-periods`),

  /**
   * Upload profile image
   */
  uploadProfileImage: (affiliateId: string, file: File): Promise<ApiResponse<{ url: string }>> => {
    const formData = new FormData()
    formData.append('image', file)
    return apiClient.post(`/affiliates/${affiliateId}/profile-image`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      // #594: allow large uploads to exceed the global 10s axios timeout.
      timeout: 120000,
    })
  },

  /**
   * #572 — upload a file to the affiliate's "My Top Media" gallery. The
   * server pipes the buffer to storage and appends the item to top_media.
   */
  uploadTopMediaFile: (
    affiliateId: string,
    file: File,
    title?: string,
  ): Promise<ApiResponse<{ id: string; url: string }>> => {
    const formData = new FormData()
    formData.append('file', file)
    if (title) formData.append('title', title)
    return apiClient.post(`/affiliates/${affiliateId}/top-media`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      // #594: allow large uploads to exceed the global 10s axios timeout.
      timeout: 120000,
    })
  },

  /**
   * #724 — add a URL link to Top Media via the same append endpoint files use,
   * so it appends server-side and never clobbers other items the way the
   * full-array PUT /me path did.
   */
  addTopMediaLink: (
    affiliateId: string,
    url: string,
    title?: string,
  ): Promise<ApiResponse<{ id: string; url: string }>> => {
    const formData = new FormData()
    formData.append('url', url)
    formData.append('type', 'link')
    if (title) formData.append('title', title)
    return apiClient.post(`/affiliates/${affiliateId}/top-media`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },

  /**
   * #724 — remove a Top Media item via the dedicated server-side delete
   * endpoint, avoiding the full-array PUT /me overwrite.
   */
  deleteTopMedia: (
    affiliateId: string,
    mediaId: string,
  ): Promise<ApiResponse<unknown>> => {
    return apiClient.delete(`/affiliates/${affiliateId}/top-media/${mediaId}`)
  },

  /**
   * #1105 — Open Graph link preview for a link-type Top Media item. The
   * backend unfurls the STORED item URL (never a caller-supplied one) and
   * returns ok=false when metadata could not be fetched, in which case the
   * modal renders the styled fallback card.
   */
  getTopMediaPreview: (
    affiliateId: string,
    mediaId: string,
  ): Promise<ApiResponse<TopMediaLinkPreview>> =>
    apiClient.get(`/affiliates/${affiliateId}/top-media/${mediaId}/preview`),

  /**
   * Get available segments for affiliates
   */
  getSegments: (): Promise<ApiResponse<{ id: string; name: string }[]>> =>
    apiClient.get('/affiliates/segments'),

  /**
   * #275 v0 — list an affiliate's self-reported creator stats.
   */
  getCreatorStats: (affiliateId: string): Promise<ApiResponse<CreatorStat[]>> =>
    apiClient.get(`/affiliates/${affiliateId}/creator-stats`),

  /**
   * #275 v0 — upsert an affiliate's self-reported creator stats. Only
   * platforms with an existing social_media handle are accepted server-side.
   */
  upsertCreatorStats: (
    affiliateId: string,
    stats: CreatorStatInput[],
  ): Promise<ApiResponse<CreatorStat[]>> =>
    apiClient.put(`/affiliates/${affiliateId}/creator-stats`, { stats }),

  /**
   * #572 — get an affiliate's Company & Bank Details. data is null when the
   * affiliate hasn't filled it in yet. Owning affiliate or admin only.
   */
  getBilling: (affiliateId: string): Promise<ApiResponse<AffiliateBilling | null>> =>
    apiClient.get(`/affiliates/${affiliateId}/billing`),

  /**
   * #572 — upsert an affiliate's Company & Bank Details. iban + swift optional.
   */
  updateBilling: (
    affiliateId: string,
    payload: AffiliateBillingInput,
  ): Promise<ApiResponse<AffiliateBilling>> =>
    apiClient.put(`/affiliates/${affiliateId}/billing`, payload),
}
